import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MaterialsComparator } from "@/components/MaterialsComparator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_KINDS = ["renovation", "construction"];
const LEVELS = [
    { v: "economique", l: "Économique" },
    { v: "standard", l: "Standard" },
    { v: "premium", l: "Premium" },
    { v: "haut_de_gamme", l: "Haut de gamme" },
];
const LOTS_RENO = [
    "demolition", "placo", "isolation", "electricite",
    "plomberie", "carrelage", "peinture", "menuiseries",
    "cuisine", "salle_de_bain",
];
const LOTS_CONST = [
    "fondations", "maconnerie", "electricite", "plomberie",
    "isolation", "placo", "carrelage", "peinture", "menuiseries", "toiture", "facade",
];

const RENDER_STYLES = [
    { v: "parisien_classique", l: "Parisien classique" },
    { v: "haussmannien_renove", l: "Haussmannien rénové" },
    { v: "contemporain_luxe", l: "Contemporain luxe" },
    { v: "minimaliste_japonais", l: "Minimaliste japonais" },
    { v: "industriel_chic", l: "Industriel chic" },
    { v: "provencal", l: "Provençal contemporain" },
    { v: "construction_finie", l: "Maison finie (construction)" },
];

const SUGGESTIONS = [
    "Je veux rénover un appartement de 70m² à Paris — cuisine + salle de bain neuves + peinture partout.",
    "Combien coûte une extension bois de 20m² sur ma maison ?",
    "Je souhaite refaire ma toiture — 120m² — que dois-je prévoir ?",
    "Puis-je poser du parquet moi-même dans un salon de 25m² ?",
];

const fmt = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n);

// naive markdown to HTML for chat replies
function renderMarkdown(md) {
    if (!md) return "";
    let html = md
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    html = html.replace(/^### (.*)$/gm, '<h4>$1</h4>');
    html = html.replace(/^## (.*)$/gm, '<h3>$1</h3>');
    html = html.replace(/^# (.*)$/gm, '<h3>$1</h3>');
    html = html.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>");
    html = html.replace(/\*(.+?)\*/g, "<i>$1</i>");
    // simple tables
    const lines = html.split("\n");
    const out = [];
    let inTable = false;
    for (let i = 0; i < lines.length; i++) {
        const ln = lines[i];
        if (/^\|.*\|/.test(ln)) {
            if (!inTable) {
                out.push('<table class="ai-table">');
                inTable = true;
            }
            if (/^\|\s*-+/.test(ln)) continue; // divider
            const cells = ln.split("|").slice(1, -1).map((c) => `<td>${c.trim()}</td>`).join("");
            out.push(`<tr>${cells}</tr>`);
        } else {
            if (inTable) {
                out.push("</table>");
                inTable = false;
            }
            out.push(ln);
        }
    }
    if (inTable) out.push("</table>");
    return out.join("<br/>");
}

export default function CmRenovAI() {
    const { user, setUser } = useAuth();
    const nav = useNavigate();
    const [sessionId] = useState(() => `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    const [messages, setMessages] = useState([
        {
            role: "ai",
            text: "Bonjour ! Je suis **CMRENOV AI**, votre copilote travaux. 👷\n\nDécrivez-moi votre projet en une phrase — ou choisissez ci-dessous — et je vous aide à l'estimer, à calculer les matériaux, et à savoir ce que vous pouvez faire vous-même.",
        },
    ]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const scrollerRef = useRef(null);

    // Estimator state
    const [surface, setSurface] = useState(80);
    const [kind, setKind] = useState("renovation");
    const [level, setLevel] = useState("premium");
    const [selLots, setSelLots] = useState(LOTS_RENO.slice(0, 8));
    const [estimate, setEstimate] = useState(null);

    // Calculator state
    const [calc, setCalc] = useState("peinture");
    const [calcSurface, setCalcSurface] = useState(30);
    const [calcResult, setCalcResult] = useState(null);

    // Renovation preview state
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [renderStyle, setRenderStyle] = useState("parisien_classique");
    const [renderCustom, setRenderCustom] = useState("");
    const [renderResult, setRenderResult] = useState(null);
    const [rendering, setRendering] = useState(false);
    const [shareBusy, setShareBusy] = useState(false);
    const [shareUrl, setShareUrl] = useState(null);
    const fileInputRef = useRef(null);

    // Quote analysis state
    const [quoteFile, setQuoteFile] = useState(null);
    const [quoteAnalysis, setQuoteAnalysis] = useState(null);
    const [quoteBusy, setQuoteBusy] = useState(false);
    const quoteInputRef = useRef(null);

    useEffect(() => {
        if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }, [messages]);

    useEffect(() => {
        const available = kind === "renovation" ? LOTS_RENO : LOTS_CONST;
        setSelLots(available.slice(0, 8));
    }, [kind]);

    const send = async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || busy) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", text: msg }]);
        setBusy(true);
        try {
            const r = await axios.post(`${API}/ai/chat`, { session_id: sessionId, message: msg });
            setMessages((m) => [...m, { role: "ai", text: r.data.reply }]);
        } catch {
            setMessages((m) => [...m, { role: "ai", text: "⚠️ Une erreur s'est produite. Merci de réessayer." }]);
        } finally {
            setBusy(false);
        }
    };

    const runEstimate = async () => {
        try {
            const r = await axios.post(`${API}/ai/estimate`, {
                surface_m2: Number(surface),
                project_kind: kind,
                level,
                lots: selLots,
            });
            setEstimate(r.data);
        } catch {
            toast.error("Erreur d'estimation");
        }
    };

    const runCalc = async () => {
        try {
            const body = { calculator: calc, surface_m2: Number(calcSurface) };
            if (calc === "peinture") { body.coats = 2; body.doors = 1; body.windows = 2; }
            if (calc === "beton") { body.length_m = 5; body.width_m = 4; body.thickness_cm = 15; }
            const r = await axios.post(`${API}/ai/materials`, body);
            setCalcResult(r.data);
        } catch {
            toast.error("Erreur de calcul");
        }
    };

    const [pdfBusy, setPdfBusy] = useState(false);
    const downloadPdf = async () => {
        if (!estimate || pdfBusy) return;
        setPdfBusy(true);
        try {
            const r = await axios.post(`${API}/ai/estimate/pdf`, {
                surface_m2: Number(surface),
                project_kind: kind,
                level,
                lots: estimate.lots,
                total_min: estimate.total_min,
                total_max: estimate.total_max,
                contingency: estimate.contingency_10pct,
                client_name: user?.full_name || null,
                client_email: user?.email || null,
            }, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `estimation-cmrenov-${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("PDF téléchargé ✓");
        } catch {
            toast.error("Erreur lors de la génération du PDF");
        } finally {
            setPdfBusy(false);
        }
    };

    const askCmRenov = () => {
        const summary = estimate
            ? `Bonjour, je souhaite un devis pour un projet de ${kind === "renovation" ? "rénovation" : "construction"} de ${surface} m² (niveau ${level}). Estimation via CMRENOV AI : ${fmt(estimate.total_min)} — ${fmt(estimate.total_max)} €. Lots : ${estimate.lots.map(l => l.label).join(", ")}.`
            : "Bonjour, je souhaite un devis suite à ma consultation avec CMRENOV AI.";
        navigator.clipboard?.writeText(summary).catch(() => {});
        toast.success("Résumé copié — collez-le dans le formulaire de devis 👇");
        setTimeout(() => {
            window.location.href = "/#devis";
        }, 600);
    };

    const onPickFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 20 * 1024 * 1024) { toast.error("Image trop lourde (max 20 Mo)"); return; }
        setPhotoFile(f);
        setPhotoPreview(URL.createObjectURL(f));
        setRenderResult(null);
        setShareUrl(null);
    };

    const shareRender = async () => {
        if (!renderResult || !photoFile || shareBusy) return;
        setShareBusy(true);
        try {
            const beforeB64 = await new Promise((res) => {
                const r = new FileReader();
                r.onload = () => res(r.result.split(",")[1]);
                r.readAsDataURL(photoFile);
            });
            const [meta, afterB64] = renderResult.split(",");
            const afterMime = (meta.match(/data:(.*?);/) || [])[1] || "image/png";
            const { data } = await axios.post(`${API}/share`, {
                before_base64: beforeB64,
                before_mime: photoFile.type || "image/jpeg",
                after_base64: afterB64,
                after_mime: afterMime,
            });
            const url = `${window.location.origin}/partage/${data.share_id}`;
            setShareUrl(url);
            navigator.clipboard?.writeText(url).catch(() => {});
            toast.success("Lien de partage créé et copié ✓");
        } catch {
            toast.error("Erreur de création du lien de partage");
        } finally {
            setShareBusy(false);
        }
    };

    const renderPhoto = async () => {
        if (!user) { toast.error("Connectez-vous pour utiliser cette fonctionnalité."); nav("/connexion"); return; }
        if ((user.render_credits ?? 0) <= 0) {
            toast.error("Crédit épuisé — achetez un rendu pour 5 €.");
            return;
        }
        if (!photoFile) { toast.error("Choisissez d'abord une photo."); return; }
        setRendering(true);
        setRenderResult(null);
        try {
            const fd = new FormData();
            fd.append("image", photoFile);
            fd.append("style", renderStyle);
            if (renderCustom.trim()) fd.append("custom_prompt", renderCustom.trim());
            const r = await axios.post(`${API}/ai/renovate-photo`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 25000,
            });
            const jobId = r.data.job_id;
            let attempts = 0;
            const poll = async () => {
                attempts += 1;
                if (attempts > 70) {
                    toast.error("La génération prend trop de temps. Réessayez dans un instant.");
                    setRendering(false);
                    return;
                }
                try {
                    const s = await axios.get(`${API}/ai/render-status/${jobId}`, { timeout: 20000 });
                    if (s.data.status === "done") {
                        setRenderResult(`data:${s.data.mime_type};base64,${s.data.image_base64}`);
                        if (typeof s.data.credits_remaining === "number") {
                            setUser({ ...user, render_credits: s.data.credits_remaining });
                        }
                        toast.success("Rendu prêt ✨");
                        setRendering(false);
                        return;
                    }
                    if (s.data.status === "error") {
                        toast.error("Erreur de génération. Votre crédit n'a pas été décompté — réessayez.");
                        setRendering(false);
                        return;
                    }
                } catch { /* réseau : on continue à interroger */ }
                setTimeout(poll, 3000);
            };
            setTimeout(poll, 4000);
        } catch (err) {
            if (err.response?.status === 402) toast.error("Crédit épuisé — achetez un rendu pour 5 €.");
            else if (err.response?.status === 401) { toast.error("Session expirée."); nav("/connexion"); }
            else toast.error("Erreur de génération. Merci de réessayer.");
            setRendering(false);
        }
    };

    const buyCredits = async (quantity = 1) => {
        if (!user) { nav("/connexion"); return; }
        try {
            const { data } = await axios.post(`${API}/payments/checkout`, {
                quantity,
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch {
            toast.error("Impossible de démarrer le paiement.");
        }
    };

    const analyzeQuote = async () => {
        if (!user) { nav("/connexion"); return; }
        if (!quoteFile) { toast.error("Choisissez un devis (PDF ou photo)"); return; }
        setQuoteBusy(true);
        setQuoteAnalysis(null);
        try {
            const fd = new FormData();
            fd.append("image", quoteFile);
            const r = await axios.post(`${API}/ai/analyze-quote`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 120000,
            });
            setQuoteAnalysis(r.data.analysis);
        } catch (e) {
            toast.error("Erreur d'analyse. Réessayez.");
        } finally {
            setQuoteBusy(false);
        }
    };

    return (
        <div data-testid="cmrenov-ai-page" className="min-h-screen bg-[#FAF9F6]">
            <Navbar />

            <main className="pt-28 pb-24">
                <div className="max-w-[1500px] mx-auto px-6 md:px-12">
                    {/* Header */}
                    <div className="mb-12 section-fade">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-[#B89C6A]" />
                            <span className="tracking-luxe text-[#B89C6A]">
                                CMRENOV AI · Phase 1 (bêta)
                            </span>
                        </div>
                        <h1 className="font-serif-lux text-4xl md:text-5xl lg:text-6xl leading-[1.02] max-w-4xl">
                            Votre <em className="italic text-[#B89C6A]">copilote</em> travaux — de l'idée au chantier.
                        </h1>
                        <p className="mt-6 max-w-2xl text-[15px] text-[#1C1E1D]/70 leading-relaxed">
                            Estimez. Comparez. Construisez. Un assistant IA propulsé par Claude Sonnet 5 vous conseille, calcule vos quantités de matériaux, vous dit ce que vous pouvez faire vous-même — et bascule vers un devis CM RENOV quand le chantier devient trop technique.
                        </p>
                    </div>

                    {/* AI RENOVATION PREVIEW — HERO FEATURE */}
                    <div className="mb-16 lux-card p-6 md:p-8 section-fade">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                            <div className="lg:col-span-4">
                                <div className="tracking-luxe text-[#B89C6A] mb-3">
                                    ✨ Aperçu IA · Avant → Après
                                </div>
                                <h2 className="font-serif-lux text-3xl md:text-4xl leading-tight mb-4">
                                    Visualisez votre <em className="italic text-[#B89C6A]">pièce rénovée</em> en 30 secondes.
                                </h2>
                                <p className="text-[13px] text-[#1C1E1D]/70 leading-relaxed">
                                    Uploadez une photo de votre pièce actuelle (ou d'un chantier en cours) → choisissez un style → l'IA génère un rendu photoréaliste de la pièce transformée par CM RENOV.
                                </p>
                                <p className="mt-4 text-[11px] text-[#1C1E1D]/40 italic">
                                    Rendu illustratif généré par IA · non-contractuel · pour visualisation uniquement.
                                </p>
                            </div>

                            <div className="lg:col-span-8 space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <div className="tracking-luxe text-[#B89C6A] mb-2 text-[10px]">Avant · Votre photo</div>
                                        <div className="aspect-[4/3] bg-[#F2EEE5] border hairline flex items-center justify-center overflow-hidden relative group">
                                            {photoPreview ? (
                                                <img src={photoPreview} alt="Original" className="w-full h-full object-cover" />
                                            ) : (
                                                <span className="text-[12px] text-[#1C1E1D]/40 italic">Aucune photo</span>
                                            )}
                                        </div>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp"
                                            onChange={onPickFile}
                                            className="hidden"
                                            data-testid="render-file-input"
                                        />
                                        <button
                                            data-testid="render-pick"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="btn-outline mt-3 w-full justify-center text-[10px]"
                                        >
                                            {photoPreview ? "Changer la photo" : "Choisir / prendre une photo"}
                                        </button>
                                    </div>
                                    <div>
                                        <div className="tracking-luxe text-[#B89C6A] mb-2 text-[10px]">Après · Vision CM RENOV</div>
                                        <div className="aspect-[4/3] bg-[#1C1E1D] border border-[#B89C6A]/30 flex items-center justify-center overflow-hidden relative">
                                            {rendering ? (
                                                <div className="text-center text-[#B89C6A]">
                                                    <div className="w-8 h-8 border-2 border-[#B89C6A] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                                                    <div className="text-[10px] tracking-widest uppercase">Rendu en cours · 20-40 s</div>
                                                </div>
                                            ) : renderResult ? (
                                                <img src={renderResult} alt="Rendu" className="w-full h-full object-cover" data-testid="render-result-img" />
                                            ) : (
                                                <span className="text-[12px] text-[#FAF9F6]/40 italic">Rendu à venir</span>
                                            )}
                                        </div>
                                        {renderResult && (
                                            <a
                                                href={renderResult}
                                                download={`cmrenov-rendu-${Date.now()}.png`}
                                                className="btn-outline mt-3 w-full justify-center text-[10px]"
                                            >
                                                Télécharger le rendu
                                            </a>
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                                    <div>
                                        <div className="tracking-luxe text-[#B89C6A] mb-2 text-[10px]">Style souhaité</div>
                                        <select
                                            data-testid="render-style"
                                            className="lux-input"
                                            value={renderStyle}
                                            onChange={(e) => setRenderStyle(e.target.value)}
                                        >
                                            {RENDER_STYLES.map((s) => (
                                                <option key={s.v} value={s.v}>{s.l}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <input
                                        data-testid="render-custom"
                                        className="lux-input"
                                        placeholder="Précisions (facultatif) : ex. cuisine ouverte, verrière…"
                                        value={renderCustom}
                                        onChange={(e) => setRenderCustom(e.target.value)}
                                    />
                                </div>

                                <button
                                    data-testid="render-run"
                                    onClick={renderPhoto}
                                    disabled={!photoFile || rendering}
                                    className="btn-champagne w-full justify-center disabled:opacity-40"
                                >
                                    {rendering ? "Génération en cours…" : (user ? `Générer le rendu ✨ (${user.render_credits ?? 0} crédit${(user.render_credits ?? 0) > 1 ? 's' : ''} restant${(user.render_credits ?? 0) > 1 ? 's' : ''})` : "Se connecter pour générer un rendu")}
                                </button>

                                {user && (user.render_credits ?? 0) <= 0 && (
                                    <div className="bg-[#B89C6A]/10 border border-[#B89C6A] p-4 mt-2">
                                        <div className="tracking-luxe text-[#B89C6A] mb-2">Crédit épuisé</div>
                                        <p className="text-[13px] text-[#1C1E1D]/80 mb-3">
                                            Vous avez utilisé vos 2 rendus gratuits. Achetez de nouveaux rendus à <b>5 € pièce</b>.
                                        </p>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[1, 3, 5].map((q) => (
                                                <button
                                                    key={q}
                                                    data-testid={`buy-${q}`}
                                                    onClick={() => buyCredits(q)}
                                                    className="border hairline hover:border-[#B89C6A] py-3 text-[11px]"
                                                >
                                                    <div className="font-serif-lux text-xl">{q}</div>
                                                    <div className="tracking-luxe">{q * 5} €</div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {renderResult && (
                                    <>
                                        <button
                                            data-testid="render-cta"
                                            onClick={askCmRenov}
                                            className="btn-outline w-full justify-center"
                                        >
                                            Ce rendu me plaît — demander un devis CM RENOV →
                                        </button>
                                        <button
                                            data-testid="render-share"
                                            onClick={shareRender}
                                            disabled={shareBusy}
                                            className="btn-champagne w-full justify-center disabled:opacity-50"
                                        >
                                            {shareBusy ? "Création du lien…" : "Partager mon avant/après 🔗"}
                                        </button>
                                        {shareUrl && (
                                            <div data-testid="share-panel" className="border border-[#B89C6A] p-4 space-y-3">
                                                <div className="tracking-luxe text-[#B89C6A] text-[10px]">Lien public copié — partagez-le :</div>
                                                <div className="flex gap-2">
                                                    <input readOnly className="lux-input flex-1 text-[11px]" value={shareUrl} onFocus={(e) => e.target.select()} data-testid="share-url" />
                                                    <button
                                                        className="btn-outline text-[10px]"
                                                        onClick={() => { navigator.clipboard?.writeText(shareUrl); toast.success("Copié ✓"); }}
                                                    >
                                                        Copier
                                                    </button>
                                                </div>
                                                <div className="grid grid-cols-3 gap-2">
                                                    <a data-testid="share-whatsapp" target="_blank" rel="noreferrer"
                                                        href={`https://wa.me/?text=${encodeURIComponent("Regardez ma pièce rénovée par l'IA de CM RENOV ✨ " + shareUrl)}`}
                                                        className="btn-outline justify-center text-[10px]">WhatsApp</a>
                                                    <a data-testid="share-facebook" target="_blank" rel="noreferrer"
                                                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                                                        className="btn-outline justify-center text-[10px]">Facebook</a>
                                                    <a data-testid="share-x" target="_blank" rel="noreferrer"
                                                        href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("Ma pièce réimaginée par l'IA de CM RENOV ✨")}&url=${encodeURIComponent(shareUrl)}`}
                                                        className="btn-outline justify-center text-[10px]">X</a>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 3-column layout: chat | estimator | calculators */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        {/* Chat */}
                        <div className="lg:col-span-6 flex flex-col lux-card min-h-[600px]">
                            <div className="p-5 border-b hairline flex items-center gap-3">
                                <span className="w-2 h-2 rounded-full bg-[#B89C6A] animate-pulse" />
                                <span className="tracking-luxe text-[#B89C6A]">
                                    CMRENOV AI · Assistant conversationnel
                                </span>
                            </div>

                            <div
                                ref={scrollerRef}
                                data-testid="ai-chat-scroller"
                                className="flex-1 overflow-y-auto p-5 space-y-4 max-h-[520px]"
                            >
                                {messages.map((m, i) => (
                                    <div
                                        key={i}
                                        className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                                    >
                                        <div
                                            className={`max-w-[85%] p-4 text-[14px] leading-relaxed ${
                                                m.role === "user"
                                                    ? "bg-[#1C1E1D] text-[#FAF9F6]"
                                                    : "bg-[#F2EEE5] text-[#1C1E1D]"
                                            }`}
                                        >
                                            {m.role === "ai" ? (
                                                <div
                                                    className="ai-md"
                                                    dangerouslySetInnerHTML={{ __html: renderMarkdown(m.text) }}
                                                />
                                            ) : (
                                                m.text
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {busy && (
                                    <div className="text-[12px] text-[#B89C6A] italic">
                                        CMRENOV AI réfléchit…
                                    </div>
                                )}
                            </div>

                            {/* Suggestions */}
                            {messages.length <= 1 && (
                                <div className="px-5 pb-3 flex flex-wrap gap-2">
                                    {SUGGESTIONS.map((s) => (
                                        <button
                                            key={s}
                                            data-testid="ai-suggestion"
                                            onClick={() => send(s)}
                                            className="text-[11px] tracking-wide border hairline px-3 py-1.5 hover:bg-[#B89C6A]/10 hover:border-[#B89C6A] transition-colors text-left"
                                        >
                                            {s.length > 60 ? s.slice(0, 58) + "…" : s}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <form
                                onSubmit={(e) => { e.preventDefault(); send(); }}
                                className="p-5 border-t hairline flex gap-3"
                            >
                                <input
                                    data-testid="ai-input"
                                    className="lux-input flex-1"
                                    placeholder="Décrivez votre projet…"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    disabled={busy}
                                />
                                <button
                                    type="submit"
                                    data-testid="ai-send"
                                    disabled={busy}
                                    className="btn-champagne disabled:opacity-50"
                                >
                                    Envoyer →
                                </button>
                            </form>
                        </div>

                        {/* Estimator + Calculators */}
                        <div className="lg:col-span-6 space-y-6">
                            {/* Estimator */}
                            <div className="lux-card p-6">
                                <div className="tracking-luxe text-[#B89C6A] mb-4">
                                    Estimation par lots
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <label className="text-[12px] tracking-wider uppercase text-[#1C1E1D]/70 col-span-2">
                                        Surface : <b>{surface} m²</b>
                                        <input
                                            type="range" min={10} max={500} step={5}
                                            value={surface}
                                            onChange={(e) => setSurface(e.target.value)}
                                            className="w-full accent-[#B89C6A] mt-2"
                                        />
                                    </label>
                                    <div className="flex gap-2">
                                        {PROJECT_KINDS.map((k) => (
                                            <button
                                                key={k}
                                                data-testid={`kind-${k}`}
                                                onClick={() => setKind(k)}
                                                className={`flex-1 py-2 border text-[11px] tracking-wider uppercase transition ${kind === k ? "border-[#B89C6A] bg-[#B89C6A]/10" : "hairline"}`}
                                            >
                                                {k === "renovation" ? "Rénovation" : "Construction"}
                                            </button>
                                        ))}
                                    </div>
                                    <select
                                        data-testid="level"
                                        className="lux-input"
                                        value={level}
                                        onChange={(e) => setLevel(e.target.value)}
                                    >
                                        {LEVELS.map((l) => (
                                            <option key={l.v} value={l.v}>{l.l}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mt-4 flex flex-wrap gap-2">
                                    {(kind === "renovation" ? LOTS_RENO : LOTS_CONST).map((lot) => {
                                        const on = selLots.includes(lot);
                                        return (
                                            <button
                                                key={lot}
                                                data-testid={`lot-${lot}`}
                                                onClick={() => setSelLots((s) => on ? s.filter((x) => x !== lot) : [...s, lot])}
                                                className={`text-[11px] px-3 py-1.5 border transition ${on ? "border-[#B89C6A] bg-[#B89C6A]/10" : "hairline"}`}
                                            >
                                                {lot.replace(/_/g, " ")}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={runEstimate}
                                    data-testid="estimate-run"
                                    className="btn-champagne mt-5 w-full justify-center"
                                >
                                    Calculer l'estimation
                                </button>

                                {estimate && (
                                    <div data-testid="estimate-result" className="mt-5 pt-5 border-t hairline">
                                        <div className="font-serif-lux text-3xl">
                                            {fmt(estimate.total_min)} €
                                            <span className="text-[#B89C6A] mx-2">—</span>
                                            {fmt(estimate.total_max)} €
                                        </div>
                                        <div className="text-[11px] text-[#1C1E1D]/50 mt-1">
                                            Aléas 10 % : {fmt(estimate.contingency_10pct)} € · IDF · TTC indicatif
                                        </div>
                                        <table className="w-full mt-4 text-[12px]">
                                            <tbody>
                                                {estimate.lots.map((l) => (
                                                    <tr key={l.lot} className="border-b hairline">
                                                        <td className="py-1.5 tracking-wider uppercase text-[10px] text-[#1C1E1D]/60">{l.label}</td>
                                                        <td className="py-1.5 text-right">
                                                            {fmt(l.cost_min)} — {fmt(l.cost_max)} €
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <div className="mt-5 flex flex-col sm:flex-row gap-3">
                                            <button
                                                data-testid="estimate-pdf-download"
                                                onClick={downloadPdf}
                                                disabled={pdfBusy}
                                                className="btn-champagne flex-1 justify-center disabled:opacity-50"
                                            >
                                                {pdfBusy ? "Génération…" : "Télécharger le PDF ↓"}
                                            </button>
                                            <button
                                                data-testid="ask-cmrenov"
                                                onClick={askCmRenov}
                                                className="btn-outline flex-1 justify-center"
                                            >
                                                Faire réaliser par CM RENOV →
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Calculators */}
                            <div className="lux-card p-6">
                                <div className="tracking-luxe text-[#B89C6A] mb-4">
                                    Calculateur de matériaux
                                </div>
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {["peinture", "placo", "carrelage", "beton", "isolation"].map((k) => (
                                        <button
                                            key={k}
                                            data-testid={`calc-${k}`}
                                            onClick={() => { setCalc(k); setCalcResult(null); }}
                                            className={`text-[11px] px-3 py-1.5 border capitalize transition ${calc === k ? "border-[#B89C6A] bg-[#B89C6A]/10" : "hairline"}`}
                                        >
                                            {k}
                                        </button>
                                    ))}
                                </div>
                                <label className="text-[12px] tracking-wider uppercase text-[#1C1E1D]/70">
                                    {calc === "beton" ? "Dalle 5 × 4 × 15 cm — préconfiguré" : `Surface : ${calcSurface} m²`}
                                    {calc !== "beton" && (
                                        <input
                                            type="range" min={5} max={200} step={1}
                                            value={calcSurface}
                                            onChange={(e) => setCalcSurface(e.target.value)}
                                            className="w-full accent-[#B89C6A] mt-2"
                                        />
                                    )}
                                </label>
                                <button
                                    onClick={runCalc}
                                    data-testid="calc-run"
                                    className="btn-champagne mt-4 w-full justify-center"
                                >
                                    Calculer les quantités
                                </button>

                                {calcResult && (
                                    <div data-testid="calc-result" className="mt-4 pt-4 border-t hairline text-[13px] space-y-1">
                                        {Object.entries(calcResult)
                                            .filter(([k]) => !["calculator", "inputs"].includes(k))
                                            .map(([k, v]) => (
                                                <div key={k} className="flex justify-between border-b hairline py-1">
                                                    <span className="tracking-wider uppercase text-[10px] text-[#1C1E1D]/60">
                                                        {k.replace(/_/g, " ")}
                                                    </span>
                                                    <span className="font-serif-lux">
                                                        {typeof v === "number" ? fmt(v) : String(v)}
                                                    </span>
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </div>

                            {/* Safety notice */}
                            <div className="bg-[#1C1E1D] text-[#FAF9F6] p-6">
                                <div className="tracking-luxe text-[#B89C6A] mb-3">
                                    🔴 Interventions professionnelles obligatoires
                                </div>
                                <p className="text-[13px] text-[#FAF9F6]/70 leading-relaxed">
                                    <b>Gaz · Tableau électrique · Mur porteur · Toiture · Amiante · Fondations</b> — CMRENOV AI vous alertera systématiquement. Ces travaux exigent une entreprise assurée et qualifiée. CM RENOV intervient sur tous ces lots avec assurance décennale et RC Pro.
                                </p>
                                <Link
                                    to="/#rendezvous"
                                    className="btn-outline mt-4 inline-flex border-[#FAF9F6]/40 text-[#FAF9F6]"
                                >
                                    Rendez-vous gratuit sur site
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Legal */}
                    <p className="mt-12 text-[11px] text-[#1C1E1D]/40 max-w-3xl">
                        Les prix affichés par CMRENOV AI sont des estimations indicatives basées sur les prix moyens Île-de-France 2025. Ils ne constituent pas un devis contractuel. Un devis officiel est établi après visite technique gratuite sur site.
                    </p>

                    {/* Analyse de devis concurrent */}
                    <div className="mt-16 lux-card p-6 md:p-8">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                            <div className="lg:col-span-4">
                                <div className="tracking-luxe text-[#B89C6A] mb-3">
                                    📋 Analyse de devis
                                </div>
                                <h2 className="font-serif-lux text-3xl leading-tight mb-4">
                                    Uploadez un devis concurrent — obtenez un <em className="italic text-[#B89C6A]">décryptage IA</em>.
                                </h2>
                                <p className="text-[13px] text-[#1C1E1D]/70 leading-relaxed">
                                    Prix trop élevés ? Éléments oubliés ? Garanties manquantes ? Notre IA compare chaque ligne au marché IDF et vous alerte sur les points à négocier.
                                </p>
                                <p className="mt-3 text-[11px] text-[#1C1E1D]/40 italic">
                                    Aucun coût — service offert.
                                </p>
                            </div>
                            <div className="lg:col-span-8">
                                <input
                                    ref={quoteInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,application/pdf"
                                    onChange={(e) => { setQuoteFile(e.target.files?.[0] || null); setQuoteAnalysis(null); }}
                                    className="hidden"
                                    data-testid="quote-file-input"
                                />
                                <button
                                    onClick={() => quoteInputRef.current?.click()}
                                    data-testid="quote-pick"
                                    className="btn-outline w-full justify-center"
                                >
                                    {quoteFile ? `📎 ${quoteFile.name.slice(0, 40)}` : "Choisir un devis (PDF ou photo)"}
                                </button>
                                {quoteFile && (
                                    <button
                                        data-testid="quote-analyze"
                                        onClick={analyzeQuote}
                                        disabled={quoteBusy}
                                        className="btn-champagne w-full justify-center mt-3 disabled:opacity-50"
                                    >
                                        {quoteBusy ? "Analyse en cours…" : "Analyser le devis"}
                                    </button>
                                )}
                                {quoteAnalysis && (
                                    <div
                                        data-testid="quote-analysis"
                                        className="mt-6 p-6 bg-[#F2EEE5] ai-md text-[13px] leading-relaxed"
                                        dangerouslySetInnerHTML={{ __html: renderMarkdown(quoteAnalysis) }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    <MaterialsComparator />
                </div>
            </main>

            <Footer />

            <style>{`
                .ai-md h3 { font-family: 'Cormorant Garamond', serif; font-size: 20px; margin: 12px 0 8px; color: #1C1E1D; }
                .ai-md h4 { font-weight: 600; margin: 10px 0 6px; }
                .ai-md b { color: #1C1E1D; }
                .ai-md table.ai-table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 12px; }
                .ai-md table.ai-table td { padding: 6px 8px; border-bottom: 1px solid rgba(184,156,106,0.25); }
                .ai-md table.ai-table tr:first-child td { font-weight: 600; color: #B89C6A; text-transform: uppercase; letter-spacing: 0.1em; font-size: 10px; }
                .ai-md ul { padding-left: 18px; margin: 6px 0; }
                .ai-md li { list-style: disc; margin: 3px 0; }
            `}</style>
        </div>
    );
}
